// src/components/admin/training/series/reschedule-modal.tsx
import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { useRescheduleSessionMutation } from "@/services/trainingApi";
import { TrainingSession } from "@/types/training";
import { toast } from "@/components/ui/use-toast";
import { AlertTriangle, CheckCircle, X } from "lucide-react";
import { formatClassDateTime, parseSessionDateTime } from "@/lib/helper";
import { format } from "date-fns";

interface RescheduleModalProps {
  session: TrainingSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const RescheduleModal: React.FC<RescheduleModalProps> = ({
  session,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [rescheduleSession, { isLoading }] = useRescheduleSessionMutation();

  const [newStartTime, setNewStartTime] = useState("");
  const [notes, setNotes] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Compute dynamic cutoff rule from package (default 48 hours)
  const cutoffHours = React.useMemo(() => {
    return (session as any)?.training_package?.reschedule_cutoff_hours ?? 48;
  }, [session]);

  const sessionDate = React.useMemo(() => {
    return parseSessionDateTime(session?.start_time);
  }, [session?.start_time]);

  const hoursUntilSession = React.useMemo(() => {
    if (!sessionDate) return 999;
    return (sessionDate.getTime() - Date.now()) / (1000 * 60 * 60);
  }, [sessionDate]);

  const isPast = hoursUntilSession <= 0;
  const isWithinCutoff = hoursUntilSession < cutoffHours;

  useEffect(() => {
    if (session?.start_time) {
      try {
        const parsed = parseSessionDateTime(session.start_time);
        if (parsed && !isNaN(parsed.getTime())) {
          setNewStartTime(format(parsed, "yyyy-MM-dd'T'HH:mm"));
        } else {
          setNewStartTime("");
        }
      } catch {
        setNewStartTime("");
      }
    }
    setNotes("");
    setErrorMessage(null);
  }, [session, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!session?.id) return;

    if (isPast) {
      setErrorMessage("Cannot reschedule a session that has already passed.");
      return;
    }

    if (isWithinCutoff) {
      setErrorMessage(
        `Cannot reschedule session: the ${cutoffHours}-hour cut-off period has passed.`
      );
      return;
    }

    if (!newStartTime) {
      toast({ variant: "destructive", description: "Please select a new date and time." });
      return;
    }

    const chosenDate = parseSessionDateTime(newStartTime);
    if (!chosenDate || chosenDate.getTime() <= Date.now()) {
      setErrorMessage("Cannot reschedule a session to a past date or time.");
      return;
    }

    try {
      const formattedTime = newStartTime.length === 16 ? `${newStartTime}:00` : newStartTime;
      await rescheduleSession({
        sessionId: session.id,
        data: {
          new_start_time: formattedTime,
          notes: notes.trim() || undefined,
        },
      }).unwrap();

      toast({
        title: "Class Rescheduled",
        description: `Session #${session.session_number} successfully rescheduled.`,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      const detail =
        err?.data?.message ||
        err?.data?.detail ||
        "Reschedule failed. Please verify that both coach and facility are available.";
      setErrorMessage(typeof detail === "string" ? detail : JSON.stringify(detail));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        hideCloseButton
        className="w-full !max-w-full sm:!max-w-[700px] py-0 custom-scrollbar h-full"
      >
        <form noValidate onSubmit={handleSubmit} className="pb-8">
          <SheetHeader className="sticky !top-0 z-40 pt-4 bg-white">
            <SheetTitle>
              <div className="flex flex-wrap justify-between gap-3 items-start bg-white">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="text-gray-500 hover:text-black transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-base sm:text-lg">
                        Reschedule Class
                      </p>
                      {isPast ? (
                        <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
                          Session Passed
                        </Badge>
                      ) : isWithinCutoff ? (
                        <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
                          &lt; {cutoffHours}h Cutoff
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-300">
                          Standard
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">Training Management</span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold">
                        Session #{session?.session_number}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted h-9 px-3"
                    onClick={() => onOpenChange(false)}
                  >
                    <i className="fa fa-xmark"></i>
                    <span className="hidden sm:inline">Cancel</span>
                  </Button>
                  <LoadingButton
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading || isWithinCutoff || isPast}
                    className={
                      isWithinCutoff || isPast
                        ? "bg-slate-300 text-slate-600 cursor-not-allowed font-semibold h-9 px-4 disabled:opacity-60"
                        : "bg-primary text-black font-semibold hover:bg-primary/90 h-9 px-4"
                    }
                  >
                    {isPast
                      ? "Session Passed"
                      : isWithinCutoff
                      ? "Reschedule Locked"
                      : "Reschedule Class"}
                  </LoadingButton>
                </div>
              </div>
            </SheetTitle>
            <Separator className="h-[1px] rounded-full my-2" />
          </SheetHeader>

          <div className="space-y-4 pt-3">
            {/* Current Session Summary */}
            <div className="p-3 bg-muted/40 rounded-lg border text-xs space-y-1.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current Time:</span>
                <span className="font-semibold">{formatClassDateTime(session?.start_time) || "Not scheduled"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assigned Trainer:</span>
                <span className="font-medium">{session?.coach_name || `Coach #${session?.coach_id}`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assigned Facility:</span>
                <span className="font-medium">{session?.facility_name || `Facility #${session?.facility_id}`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Package Cut-off Rule:</span>
                <span className="font-medium text-foreground">{cutoffHours} hours notice required</span>
              </div>
            </div>

            {/* Cutoff Warning / Status Banner */}
            {isPast ? (
              <Alert variant="destructive" className="bg-red-50 border-red-300 text-red-900">
                <AlertTriangle className="size-4 text-red-700 mt-0.5" />
                <div className="ml-2">
                  <AlertTitle className="text-xs font-bold text-red-800">
                    Session Already Occurred
                  </AlertTitle>
                  <AlertDescription className="text-xs text-red-700 mt-1 leading-relaxed">
                    This class was scheduled for <strong>{formatClassDateTime(session?.start_time)}</strong> and has already occurred or started. Completed or past sessions cannot be rescheduled.
                  </AlertDescription>
                </div>
              </Alert>
            ) : isWithinCutoff ? (
              <Alert variant="destructive" className="bg-red-50 border-red-300 text-red-900">
                <AlertTriangle className="size-4 text-red-700 mt-0.5" />
                <div className="ml-2">
                  <AlertTitle className="text-xs font-bold text-red-800">
                    Reschedule Cut-off Enforced ({cutoffHours}-Hour Policy)
                  </AlertTitle>
                  <AlertDescription className="text-xs text-red-700 mt-1 leading-relaxed">
                    This class starts in <strong>{Math.max(0, hoursUntilSession).toFixed(1)} hours</strong> (less than the required <strong>{cutoffHours}-hour</strong> notice window). Per club policy, sessions cannot be rescheduled within the cut-off period to prevent disruptions to trainer schedules and facility bookings.
                  </AlertDescription>
                </div>
              </Alert>
            ) : (
              <div className="flex items-center gap-2 p-2.5 bg-emerald-50 rounded border border-emerald-200 text-xs text-emerald-800">
                <CheckCircle className="size-4 text-emerald-600" />
                <span>
                  Eligible for standard reschedule (<strong>{hoursUntilSession.toFixed(0)} hours</strong> before start; {cutoffHours}-hour cut-off applies).
                </span>
              </div>
            )}

            {/* API Error Notification */}
            {errorMessage && (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertTitle className="text-xs font-bold">Reschedule Rejected</AlertTitle>
                <AlertDescription className="text-xs mt-1">{errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Form Fields */}
            <div className="space-y-4 pt-1">
              <div className="space-y-1.5">
                <Label htmlFor="reschedule-time">
                  New Date & Start Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="reschedule-time"
                  type="datetime-local"
                  min={new Date().toISOString().slice(0, 16)}
                  value={newStartTime}
                  onChange={(e) => setNewStartTime(e.target.value)}
                  disabled={isWithinCutoff || isPast}
                  required
                  className="h-10 text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  End time will be auto-calculated from the package slot duration.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reschedule-notes">
                  Notes (Optional)
                </Label>
                <Textarea
                  id="reschedule-notes"
                  rows={4}
                  placeholder={
                    isWithinCutoff || isPast
                      ? "Rescheduling is locked for this session."
                      : "Reason for reschedule..."
                  }
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isWithinCutoff || isPast}
                />
              </div>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default RescheduleModal;